/**
 * SH PDP V1 — product page enhancements.
 *
 * Independent modules, each inert unless its markup is on the page:
 *   1. Founder story player  (.sh-story)
 *   2. Mobile gallery chrome (.sh-gallery-chrome)
 *   3. Rating badge label    (.jdgm-preview-badge)
 *   4. Rating scroll offset  (.sh-rating, .sh-photos__rating)
 *   5. Price mirror          (.sh-price)
 *   6. Customer photos       ([data-sh-photos])
 *   7. Accordion open/close  (details.sh-accordion)
 */
(function () {
  'use strict';

  // Both the gallery snippet and the features section request this file, so
  // the tag can appear twice on a product page.
  if (window.__shPdpV1) return;
  window.__shPdpV1 = true;

  var MOBILE_MQ = '(max-width: 999px)';

  function isMobile() {
    return window.matchMedia(MOBILE_MQ).matches;
  }

  /* =========================================================================
     Founder story player
     ========================================================================= */

  var STORY_READY = 'data-sh-story-ready';

  function setPlayingState(story, playing) {
    story.classList.toggle('is-playing', playing);

    var button = story.querySelector('.sh-story__play');
    if (!button) return;

    button.setAttribute('aria-hidden', playing ? 'true' : 'false');
    button.tabIndex = playing ? -1 : 0;
  }

  function initStory(story) {
    if (story.getAttribute(STORY_READY) === '1') return;

    var video = story.querySelector('.sh-story__video');
    var media = story.querySelector('.sh-story__media');
    var button = story.querySelector('.sh-story__play');
    if (!video || !media || !button) return;

    story.setAttribute(STORY_READY, '1');

    // Markup ships with controls as a no-JS fallback; the poster hides them.
    video.removeAttribute('controls');

    function play() {
      setPlayingState(story, true);
      video.setAttribute('controls', '');

      var playing = video.play();
      if (playing && typeof playing.catch === 'function') {
        playing.catch(function () {
          setPlayingState(story, false);
          video.removeAttribute('controls');
        });
      }
    }

    function syncPausedState() {
      if (!video.paused && !video.ended) return;
      setPlayingState(story, false);
      video.removeAttribute('controls');
    }

    button.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      play();
    });

    media.addEventListener('click', function (event) {
      if (event.target === button || button.contains(event.target)) return;
      if (story.classList.contains('is-playing')) return;
      play();
    });

    video.addEventListener('pause', syncPausedState);
    video.addEventListener('ended', syncPausedState);
    video.addEventListener('play', function () {
      setPlayingState(story, true);
    });
  }

  /* =========================================================================
     Mobile gallery chrome — counter badge + sliding pagination dots
     ========================================================================= */

  var COUNTER_CLASS = 'sh-gallery-counter';
  var VIEWPORT_CLASS = 'sh-gallery-dots-viewport';
  var TRACK_CLASS = 'sh-gallery-dots-track';
  var GALLERY_READY = 'data-sh-gallery-ready';

  function readGalleryConfig(section) {
    var marker = section.querySelector('.sh-gallery-chrome');
    if (!marker) return null;

    var dotWindow = parseInt(marker.getAttribute('data-dot-window'), 10);

    return {
      counter: marker.getAttribute('data-counter') === 'true',
      dots: marker.getAttribute('data-dots') === 'true',
      dotWindow: dotWindow > 0 ? dotWindow : 5,
    };
  }

  function getVisibleItems(media) {
    return Array.prototype.slice.call(
      media.querySelectorAll('.product__media-item:not(.is-filtered)')
    );
  }

  function getVisibleDots(media) {
    return Array.prototype.slice.call(
      media.querySelectorAll('.product__media-nav .dots-nav__item:not(.is-filtered)')
    );
  }

  function getSelectedIndex(items) {
    for (var i = 0; i < items.length; i++) {
      if (items[i].classList.contains('is-selected')) return i;
    }
    return 0;
  }

  function ensureCounter(wrapper) {
    var el = wrapper.querySelector('.' + COUNTER_CLASS);
    if (el) return el;

    el = document.createElement('span');
    el.className = COUNTER_CLASS;
    el.setAttribute('aria-live', 'polite');
    el.setAttribute('aria-atomic', 'true');
    wrapper.appendChild(el);
    return el;
  }

  /**
   * Wrap the theme's dots in a clipped viewport plus a translating track, so
   * the strip can slide instead of wrapping onto a second line.
   */
  function ensureDotsTrack(media) {
    var nav = media.querySelector('.product__media-nav .dots-nav');
    if (!nav) return null;

    nav.classList.add(VIEWPORT_CLASS);

    var track = nav.querySelector('.' + TRACK_CLASS);
    if (!track) {
      track = document.createElement('div');
      track.className = TRACK_CLASS;
      while (nav.firstChild) track.appendChild(nav.firstChild);
      nav.appendChild(track);
    }

    return { nav: nav, track: track };
  }

  function getDotStep(dots) {
    if (!dots.length) return 0;

    var style = window.getComputedStyle(dots[0]);
    var margin = (parseFloat(style.marginLeft) || 0) + (parseFloat(style.marginRight) || 0);
    return dots[0].offsetWidth + margin;
  }

  /**
   * Keep a dotWindow-wide viewport and translate the track so the active dot
   * stays visible. The active dot is held in the second slot, so the strip
   * starts moving early enough to read as motion.
   */
  function updateDotsWindow(media, config) {
    if (!config.dots) return;

    var parts = ensureDotsTrack(media);
    if (!parts) return;

    var nav = parts.nav;
    var track = parts.track;
    var dots = getVisibleDots(media);
    var total = dots.length;

    if (!isMobile() || total === 0) {
      nav.style.width = '';
      track.style.transform = '';
      return;
    }

    var step = getDotStep(dots);
    if (!step) {
      requestAnimationFrame(function () {
        updateDotsWindow(media, config);
      });
      return;
    }

    nav.style.width = Math.min(config.dotWindow, total) * step + 'px';

    if (total <= config.dotWindow) {
      track.style.transform = 'translate3d(0,0,0)';
      return;
    }

    var items = getVisibleItems(media);
    var activeItem = items[getSelectedIndex(items)];
    var activeIndex = getSelectedIndex(items);

    if (activeItem) {
      var mediaId = activeItem.getAttribute('data-media-id');
      for (var i = 0; i < dots.length; i++) {
        if (dots[i].getAttribute('data-media-id') === mediaId) {
          activeIndex = i;
          break;
        }
      }
    }

    var maxStart = Math.max(0, total - config.dotWindow);
    var start = Math.max(0, Math.min(activeIndex - 1, maxStart));
    var offset = start * step;
    var isRtl = document.documentElement.getAttribute('dir') === 'rtl';

    track.style.transform = isRtl
      ? 'translate3d(' + offset + 'px,0,0)'
      : 'translate3d(' + -offset + 'px,0,0)';
  }

  function updateGallery(media, config) {
    updateDotsWindow(media, config);
    if (!config.counter) return;

    var counter = media.querySelector('.' + COUNTER_CLASS);

    if (!isMobile()) {
      if (counter) counter.hidden = true;
      return;
    }

    var wrapper = media.querySelector('.product__media-list-wrapper');
    if (!wrapper) return;

    var items = getVisibleItems(media);
    counter = ensureCounter(wrapper);

    if (items.length < 2) {
      counter.hidden = true;
      return;
    }

    counter.hidden = false;
    counter.textContent = getSelectedIndex(items) + 1 + '/' + items.length;
  }

  function initGallery(section) {
    var config = readGalleryConfig(section);
    if (!config || (!config.counter && !config.dots)) return;

    var media = section.querySelector('product-media.product__media');
    if (!media || media.getAttribute(GALLERY_READY) === '1') return;

    media.setAttribute(GALLERY_READY, '1');

    var sync = function () {
      updateGallery(media, config);
    };

    var carousel = media.querySelector('flickity-carousel.product__media-list');
    if (carousel) {
      carousel.addEventListener('flickity:slide-changed', sync);
      carousel.addEventListener('flickity:ready', sync);
    }

    var formId = media.getAttribute('form-id');
    var form = formId ? document.getElementById(formId) : null;
    if (form) {
      form.addEventListener('variant:changed', function () {
        requestAnimationFrame(sync);
      });
    }

    window.addEventListener('resize', sync);
    window.matchMedia(MOBILE_MQ).addEventListener('change', sync);

    sync();
  }

  /* =========================================================================
     Rating badge label

     Judge.me prints "<b>5.0</b> | 250 Bewertungen", where the separator and
     the review count share a single text node. Split it so the two can be
     styled apart. The separator glyph itself is left to CSS, which keeps it a
     theme setting instead of hard-coding it here.
     ========================================================================= */

  var RATING_LABEL = '.jdgm-prev-badge__text';
  var RATING_WATCHED = 'data-sh-rating-watched';
  var RATING_MAX_PASSES = 20;
  var RATING_MAX_CALLBACKS = 40;
  var RATING_WATCH_MS = 15000;
  var RATING_VISIBLE = 'data-sh-rating-visible';

  function syncRatingShell(badge) {
    var shell = badge.closest('.sh-rating');
    if (!shell) return;

    var hidden =
      badge.style.display === 'none' ||
      window.getComputedStyle(badge).display === 'none' ||
      !badge.querySelector('.jdgm-prev-badge, .jdgm-star');

    if (hidden) shell.removeAttribute(RATING_VISIBLE);
    else shell.setAttribute(RATING_VISIBLE, '');
  }

  function splitRatingLabel(label) {
    // Testing for the span rather than a flag matters: Judge.me rewrites the
    // label's innerHTML during its own setup, which drops the spans but keeps
    // the element, so a flag set on it would leave the label unsplit.
    if (label.querySelector('.sh-rating__count')) return false;

    var nodes = label.childNodes;

    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i];
      if (node.nodeType !== 3 || node.textContent.indexOf('|') === -1) continue;

      var count = node.textContent.split('|').pop().trim();
      if (!count) continue;

      var separator = document.createElement('span');
      separator.className = 'sh-rating__sep';
      separator.setAttribute('aria-hidden', 'true');

      var reviews = document.createElement('span');
      reviews.className = 'sh-rating__count';
      reviews.textContent = count;

      var replacement = document.createDocumentFragment();
      replacement.appendChild(separator);
      replacement.appendChild(reviews);

      label.replaceChild(replacement, node);
      return true;
    }

    return false;
  }

  function initRatingBadge(badge) {
    if (badge.getAttribute(RATING_WATCHED)) return;
    badge.setAttribute(RATING_WATCHED, 'true');

    var passes = 0;
    var callbacks = 0;
    var observer = null;

    var stop = function () {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    };

    var apply = function () {
      if (++callbacks > RATING_MAX_CALLBACKS) {
        stop();
        return;
      }

      var labels = badge.querySelectorAll(RATING_LABEL);

      for (var i = 0; i < labels.length; i++) {
        if (!splitRatingLabel(labels[i])) continue;

        // Judge.me should only rewrite the label once, while setting itself up.
        // Repeated rewrites would mean we are fighting it, so back off instead
        // of trading mutations with it forever.
        if (++passes > RATING_MAX_PASSES) {
          stop();
          return;
        }
      }

      syncRatingShell(badge);
    };

    apply();

    observer = new MutationObserver(apply);
    observer.observe(badge, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style']
    });

    setTimeout(stop, RATING_WATCH_MS);
  }

  /* =========================================================================
     Rating badge scroll

     Judge.me scrolls the review widget to a fixed 30px from the top, which
     leaves its heading under the sticky header, and it ignores
     scroll-margin-top. The click is taken over here so the browser does the
     scrolling instead, which lets the offset live in CSS where it can follow
     the header at any screen size.
     ========================================================================= */

  var REVIEWS_ID = 'judgeme_product_reviews';
  var SCROLL_BOUND = 'data-sh-rating-scroll-bound';

  /**
   * Height of whatever is pinned across the top of the viewport right now.
   *
   * Measured rather than read from --header-height because the announcement
   * bar above the header is a separate sticky section that the theme's
   * variables do not describe, and because it all changes with screen size.
   */
  function measureStickyChrome() {
    var candidates = [];
    var elements = document.body.querySelectorAll('*');

    for (var i = 0; i < elements.length; i++) {
      var style = getComputedStyle(elements[i]);
      if (style.position !== 'fixed' && style.position !== 'sticky') continue;
      if (style.visibility === 'hidden' || style.opacity === '0') continue;

      var rect = elements[i].getBoundingClientRect();

      // Skip full-height overlays (cookie banners, modals), anything pinned to
      // the bottom, and narrow widgets — none of those are top chrome.
      if (rect.height < 1 || rect.height > window.innerHeight * 0.5) continue;
      if (rect.top > 200 || rect.bottom <= 0) continue;
      if (rect.width < window.innerWidth * 0.5) continue;

      candidates.push(rect);
    }

    // Walk down from the top so a pinned bar sitting above a pinned header
    // counts as one continuous band.
    var bottom = 0;
    var extended = true;

    while (extended) {
      extended = false;

      for (var j = 0; j < candidates.length; j++) {
        if (candidates[j].top <= bottom + 1 && candidates[j].bottom > bottom) {
          bottom = candidates[j].bottom;
          extended = true;
        }
      }
    }

    return bottom;
  }

  function initRatingScroll(block) {
    if (block.getAttribute(SCROLL_BOUND)) return;
    block.setAttribute(SCROLL_BOUND, 'true');

    block.addEventListener(
      'click',
      function (event) {
        var reviews = document.getElementById(REVIEWS_ID);

        // No reviews section on this page, so leave Judge.me to it.
        if (!reviews) return;

        // Capture phase: Judge.me binds to the stars themselves, so stopping
        // the event here means its own handler never runs.
        event.preventDefault();
        event.stopPropagation();

        // The snippet adds the merchant's breathing room on top of this.
        reviews.style.setProperty('--sh-sticky-chrome', measureStickyChrome() + 'px');
        reviews.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },
      true
    );
  }

  /* =========================================================================
     Price block

     The price stays where the theme put it, because ProductMeta._updatePrices
     finds it with a querySelector scoped to the product-meta element and would
     stop updating it the moment it were moved. It is mirrored into the block
     instead, and re-mirrored whenever the theme rewrites it, so variant
     changes keep working.

     The PayPal message and the tax note do not react to anything, so those are
     moved rather than copied.
     ========================================================================= */

  var PRICE_BOUND = 'data-sh-price-bound';
  var PAYPAL_SELECTOR = '[id^="zoid-paypal-message"]';

  function discountPercent(variant) {
    if (!variant || !(variant.compare_at_price > variant.price)) return null;
    return Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price);
  }

  function applyDiscountBadge(slot, variant) {
    var list = slot.querySelector('.label-list, .product-meta__label-list');
    if (!list) return;

    if (list.querySelector('.label--subdued') && !(variant && variant.available)) return;

    var pct = discountPercent(variant);
    var badge = list.querySelector('.label--highlight');

    if (pct == null) {
      if (!variant && badge) {
        var match = (badge.textContent || '').match(/(\d+)\s*%/);
        if (match) badge.textContent = '- ' + match[1] + ' %';
        return;
      }
      if (badge) badge.remove();
      return;
    }

    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'label label--highlight';
      list.appendChild(badge);
    }
    badge.textContent = '- ' + pct + ' %';
  }

  function mirrorPrice(source, slot, variant) {
    var copy = source.cloneNode(true);

    // Drop the hooks the theme and apps search on, so only the original is
    // ever found and updated.
    var marked = copy.querySelectorAll('[data-product-price-list], [data-product-label-list]');
    for (var i = 0; i < marked.length; i++) {
      marked[i].removeAttribute('data-product-price-list');
      marked[i].removeAttribute('data-product-label-list');
    }
    copy.removeAttribute('id');

    slot.innerHTML = '';
    slot.appendChild(copy);
    applyDiscountBadge(slot, variant);
  }

  function movePriceExtras(meta, extras) {
    var tax = meta.querySelector(':scope > .product-meta__taxes-included');
    if (tax) extras.appendChild(tax);

    // Climb from PayPal's message to whichever child of product-meta holds it.
    var paypal = meta.querySelector(PAYPAL_SELECTOR);
    while (paypal && paypal.parentElement && paypal.parentElement !== meta) {
      paypal = paypal.parentElement;
    }
    if (!paypal || paypal.parentElement !== meta) return;

    // PayPal sits between the price and the tax note, but it turns up long
    // after the note has moved, so it has to be inserted rather than appended.
    extras.insertBefore(paypal, extras.querySelector('.product-meta__taxes-included'));
  }

  function initPriceBlock(block) {
    var info = block.closest('.product__info');
    var meta = info && info.querySelector('product-meta');
    var slot = block.querySelector('[data-sh-price-slot]');
    var extras = block.querySelector('[data-sh-price-extras]');
    if (!meta || !slot || !extras) return;

    var source = meta.querySelector(':scope > .product-meta__price-list-container');
    if (!source) return;

    // Replaces the block's server-rendered fallback with the theme's live
    // markup, which is what makes variant changes show up.
    var lastVariant = null;
    mirrorPrice(source, slot, lastVariant);

    if (block.getAttribute(PRICE_BOUND)) return;
    block.setAttribute(PRICE_BOUND, 'true');

    // Product pages add body.WW-0001_PDP, and theme.js then empties the
    // discount label on every variant change (the old test used "DU SPARST"
    // instead). The mirror would copy that empty list, so the badge has to
    // be written back from the variant payload.
    var formId = meta.getAttribute('form-id');
    var form = formId ? document.getElementById(formId) : null;
    if (form) {
      form.addEventListener('variant:changed', function (event) {
        lastVariant = event.detail && event.detail.variant;
        mirrorPrice(source, slot, lastVariant);
      });
    }

    var priceQueued = false;
    new MutationObserver(function () {
      if (priceQueued) return;
      priceQueued = true;
      window.requestAnimationFrame(function () {
        priceQueued = false;
        mirrorPrice(source, slot, lastVariant);
      });
    }).observe(source, { childList: true, subtree: true });

    // PayPal puts its wrapper in place first and fills it in later, so the
    // arrival shows up as a change deep inside product-meta rather than a new
    // child of it. Hence subtree, and hence watching at all rather than just
    // sweeping once.
    var extrasWatcher = new MutationObserver(function () {
      movePriceExtras(meta, extras);

      if (extras.querySelector(PAYPAL_SELECTOR)) extrasWatcher.disconnect();
    });

    movePriceExtras(meta, extras);
    extrasWatcher.observe(meta, { childList: true, subtree: true });

    // Stop listening even if PayPal never turns up, so a blocked script does
    // not leave an observer running against every mutation for the session.
    setTimeout(function () {
      extrasWatcher.disconnect();
    }, 20000);
  }

  /* =========================================================================
     Echte Kundenfotos — Judge.me review photos + lightbox

     Judge.me renders its photo gallery into the review widget after load and
     exposes no photo metafield, so the grid has to be filled from the client.
     The rating summary next to it does not: snippets/sh-customer-photos.liquid
     prints that from product.metafields.reviews.
     ========================================================================= */

  var PHOTOS_BOUND = 'data-sh-photos-bound';
  var PHOTOS_POLL_MS = 300;
  var PHOTOS_POLL_TRIES = 50;

  var lightbox = { photos: [], index: 0, el: null };

  function upgradeJudgemeUrl(url, width) {
    if (!url) return '';
    if (/([?&])width=\d+/.test(url)) {
      return url.replace(/([?&])width=\d+/g, '$1width=' + width);
    }
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  function findJudgemeGallery() {
    var wrapper = document.querySelector(
      '.jdgm-row-media .jdgm-gallery-wrapper, .jdgm-gallery-wrapper'
    );
    var root = wrapper
      ? wrapper.querySelector('.jdgm-gallery') || wrapper
      : document.querySelector('.jdgm-rev-widg .jdgm-gallery, .jdgm-gallery');

    return { wrapper: wrapper, root: root };
  }

  function collectJudgemePhotos() {
    var gallery = findJudgemeGallery();
    var scope = gallery.wrapper || gallery.root;
    if (!scope) return [];

    var photos = [];
    var seen = {};

    function push(thumb, full, name) {
      var hi = upgradeJudgemeUrl(full || thumb, 1024);
      var mid = upgradeJudgemeUrl(full || thumb, 640);
      var lo = upgradeJudgemeUrl(thumb || full, 320);
      if (!hi || seen[hi]) return;

      seen[hi] = true;
      photos.push({
        thumb: lo || mid || hi,
        display: mid || hi,
        src: hi,
        name: name || '',
      });
    }

    var thumbs = scope.querySelectorAll('img.jdgm-gallery__thumbnail');

    for (var i = 0; i < thumbs.length; i++) {
      var img = thumbs[i];
      var thumb =
        img.getAttribute('src') || img.currentSrc || img.getAttribute('data-src') || '';

      // Judge.me uses an inline placeholder until the row scrolls into view.
      if (!thumb || thumb.indexOf('data:') === 0) continue;

      var full =
        img.getAttribute('data-mfo-src') || img.getAttribute('data-src-original') || '';
      push(thumb, full || thumb, '');
    }

    // The gallery ships as JSON before Judge.me hydrates the thumbnails, which
    // is the state the first pass usually sees. It also carries the reviewer
    // names, which the thumbnails do not.
    if (!photos.length && gallery.root) {
      var payload = gallery.root.querySelector('.jdgm-gallery-data[data-json]');

      if (payload) {
        try {
          var reviews = JSON.parse(payload.getAttribute('data-json') || '[]');

          for (var r = 0; r < reviews.length; r++) {
            var review = reviews[r] || {};
            var pictures = review.pictures_urls || [];

            for (var p = 0; p < pictures.length; p++) {
              if (!pictures[p]) continue;
              push(pictures[p].compact, pictures[p].original, review.reviewer_name || '');
            }
          }
        } catch (e) {
          /* Malformed payload; the thumbnail pass covers it once it hydrates. */
        }
      }
    }

    return photos;
  }

  function sizeLightboxToImage() {
    var root = lightbox.el;
    if (!root) return;

    var dialog = root.querySelector('.sh-photos-lightbox__dialog');
    var frame = root.querySelector('.sh-photos-lightbox__frame');
    var media = root.querySelector('.sh-photos-lightbox__media');
    var img = root.querySelector('.sh-photos-lightbox__img');
    if (!dialog || !media || !img || !img.naturalWidth || !img.naturalHeight) return;

    // Fit the photo's own aspect ratio inside the dialog's budget, so portrait
    // and landscape shots both fill the frame instead of being letterboxed.
    var maxHeight = Math.min(window.innerHeight * 0.68, 560);
    var maxWidth = Math.min(window.innerWidth - 56, 420);
    var ratio = img.naturalWidth / img.naturalHeight;
    var height = maxHeight;
    var width = height * ratio;

    if (width > maxWidth) {
      width = maxWidth;
      height = width / ratio;
    }

    dialog.style.width = Math.round(width) + 'px';
    media.style.height = Math.round(height) + 'px';

    // The arrows straddle the photo's left and right edges, so they follow the
    // photo's centre rather than the dialog's, which the header offsets.
    var prev = root.querySelector('[data-sh-photos-prev]');
    var next = root.querySelector('[data-sh-photos-next]');
    if (!frame || !prev || !next) return;

    var header = frame.querySelector('.sh-photos-lightbox__header');
    var centre = (header ? header.offsetHeight : 0) + height / 2;
    prev.style.top = centre + 'px';
    next.style.top = centre + 'px';
  }

  function showLightboxIndex(index) {
    var photos = lightbox.photos;
    if (!photos.length) return;

    var count = photos.length;
    var next = ((index % count) + count) % count;
    lightbox.index = next;

    var root = ensureLightbox();
    var photo = photos[next];
    var img = root.querySelector('.sh-photos-lightbox__img');
    var name = root.querySelector('.sh-photos-lightbox__name');
    var avatar = root.querySelector('.sh-photos-lightbox__avatar');

    img.onload = sizeLightboxToImage;
    img.src = photo.src;
    img.alt = photo.name ? 'Kundenfoto von ' + photo.name : 'Kundenfoto';
    name.textContent = photo.name || 'Kunde';
    avatar.textContent = (photo.name || 'K').charAt(0).toUpperCase();

    root.querySelector('[data-sh-photos-prev]').hidden = count < 2;
    root.querySelector('[data-sh-photos-next]').hidden = count < 2;

    // Cached images fire no load event.
    if (img.complete && img.naturalWidth) sizeLightboxToImage();
  }

  function closeLightbox() {
    if (!lightbox.el) return;

    lightbox.el.hidden = true;
    document.body.classList.remove('sh-photos-lightbox-open');
  }

  function ensureLightbox() {
    if (lightbox.el) return lightbox.el;

    var root = document.createElement('div');
    root.className = 'sh-photos-lightbox';
    root.hidden = true;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Kundenfoto');

    // The arrows are children of the dialog, not of the frame: the frame is
    // overflow:hidden for its rounded corners and would clip them.
    root.innerHTML =
      '<div class="sh-photos-lightbox__backdrop" data-sh-photos-close></div>' +
      '<div class="sh-photos-lightbox__dialog">' +
      '<div class="sh-photos-lightbox__frame">' +
      '<div class="sh-photos-lightbox__header">' +
      '<div class="sh-photos-lightbox__author">' +
      '<span class="sh-photos-lightbox__avatar" aria-hidden="true"></span>' +
      '<span class="sh-photos-lightbox__name"></span>' +
      '</div>' +
      '<button type="button" class="sh-photos-lightbox__close" data-sh-photos-close aria-label="Schließen">' +
      '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M4.5 4.5l9 9M13.5 4.5l-9 9" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>' +
      '</button>' +
      '</div>' +
      '<div class="sh-photos-lightbox__media">' +
      '<img class="sh-photos-lightbox__img" alt="">' +
      '</div>' +
      '</div>' +
      '<button type="button" class="sh-photos-lightbox__nav sh-photos-lightbox__nav--prev" data-sh-photos-prev aria-label="Vorheriges Foto">' +
      '<svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M12.5 4.5L7 10l5.5 5.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>' +
      '<button type="button" class="sh-photos-lightbox__nav sh-photos-lightbox__nav--next" data-sh-photos-next aria-label="Nächstes Foto">' +
      '<svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M7.5 4.5L13 10l-5.5 5.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
      '</button>' +
      '</div>';

    document.body.appendChild(root);

    root.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;

      if (target.closest('[data-sh-photos-close]')) closeLightbox();
      else if (target.closest('[data-sh-photos-prev]')) showLightboxIndex(lightbox.index - 1);
      else if (target.closest('[data-sh-photos-next]')) showLightboxIndex(lightbox.index + 1);
    });

    document.addEventListener('keydown', function (event) {
      if (root.hidden) return;

      if (event.key === 'Escape') closeLightbox();
      else if (event.key === 'ArrowLeft') showLightboxIndex(lightbox.index - 1);
      else if (event.key === 'ArrowRight') showLightboxIndex(lightbox.index + 1);
    });

    window.addEventListener('resize', function () {
      if (!root.hidden) sizeLightboxToImage();
    });

    lightbox.el = root;
    return root;
  }

  function openLightbox(photos, index) {
    lightbox.photos = photos;
    ensureLightbox();
    showLightboxIndex(index || 0);
    lightbox.el.hidden = false;
    document.body.classList.add('sh-photos-lightbox-open');
  }

  function fillPhotoGrid(gallery, photos) {
    var grid = gallery.querySelector('[data-sh-photos-grid]');
    if (!grid) return;

    var max = parseInt(gallery.getAttribute('data-max'), 10) || 8;
    var list = photos.slice(0, max);

    // Rebuilding on every poll would drop the images mid-download, so the set
    // is fingerprinted and left alone when it has not changed.
    var fingerprint = list
      .map(function (photo) {
        return photo.display || photo.src;
      })
      .join('|');

    if (grid.getAttribute('data-sh-fingerprint') === fingerprint) return;
    grid.setAttribute('data-sh-fingerprint', fingerprint);

    // The layout for counts below eight is picked by this, in sh-pdp-v1.css.
    grid.setAttribute('data-count', String(list.length));
    grid.innerHTML = '';

    list.forEach(function (photo, index) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'sh-photos__item';
      button.setAttribute('role', 'listitem');
      button.setAttribute(
        'aria-label',
        photo.name ? 'Kundenfoto von ' + photo.name + ' öffnen' : 'Kundenfoto öffnen'
      );

      var img = document.createElement('img');

      // Judge.me's own thumbnail is 160px wide and visibly soft in the hero
      // tiles, so the mid size is used with the full size for retina.
      img.src = photo.display || photo.src;
      img.srcset = (photo.display || photo.src) + ' 1x, ' + (photo.src || photo.display) + ' 2x';
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';

      button.appendChild(img);
      button.addEventListener('click', function () {
        openLightbox(list, index);
      });

      grid.appendChild(button);
    });

    gallery.hidden = false;
  }

  function initCustomerPhotos(gallery) {
    if (gallery.getAttribute(PHOTOS_BOUND)) return;
    gallery.setAttribute(PHOTOS_BOUND, 'true');

    var tries = 0;

    function pass() {
      var photos = collectJudgemePhotos();

      if (photos.length) {
        fillPhotoGrid(gallery, photos);
        return true;
      }

      return false;
    }

    if (pass()) return;

    // Judge.me loads its widget well after this script, and lazily at that, so
    // there is nothing to observe yet — hence polling rather than an observer.
    var timer = setInterval(function () {
      tries += 1;

      if (pass() || tries >= PHOTOS_POLL_TRIES) clearInterval(timer);
    }, PHOTOS_POLL_MS);
  }

  /* =========================================================================
     Accordion open / close

     Native <details> snaps shut, and animating grid-template-rows 0fr→1fr
     is jumpy (Safari skips the interpolation). Height is tweened in pixels
     instead so DETAILS, FAQ and GPSR ease the same way. The open attribute
     stays on until the close tween finishes. Opening one FAQ row still
     closes the others.
     ========================================================================= */

  var ACC_BOUND = 'data-sh-acc';
  var ACC_MS = 400;
  var ACC_EASE = 'cubic-bezier(0.33, 1, 0.68, 1)';

  function accordionPanel(details) {
    return details.querySelector(':scope > .sh-accordion__panel');
  }

  function accordionContentHeight(panel) {
    if (!panel) return 0;
    var body = panel.querySelector(':scope > .sh-accordion__body');
    if (body) return body.getBoundingClientRect().height;
    var prev = panel.style.height;
    panel.style.height = 'auto';
    var height = panel.scrollHeight;
    panel.style.height = prev;
    return height;
  }

  function accordionReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function accordionClearTween(panel) {
    if (!panel) return;
    if (panel._shAccOnEnd) {
      panel.removeEventListener('transitionend', panel._shAccOnEnd);
      panel._shAccOnEnd = null;
    }
    if (panel._shAccTimer) {
      window.clearTimeout(panel._shAccTimer);
      panel._shAccTimer = null;
    }
  }

  function accordionTween(panel, from, to, done) {
    accordionClearTween(panel);

    var distance = Math.abs(to - from);
    var ms = Math.min(700, Math.max(380, Math.round(300 + distance * 0.4)));

    if (!panel || accordionReducedMotion() || distance < 1) {
      if (panel) {
        panel.style.transition = 'none';
        panel.style.height = to + 'px';
      }
      if (done) done();
      return;
    }

    panel.style.overflow = 'hidden';
    panel.style.transition = 'none';
    panel.style.height = from + 'px';
    panel.offsetHeight;
    panel.style.transition = 'height ' + ms + 'ms ' + ACC_EASE;
    panel.style.height = to + 'px';

    var finished = false;
    function finish(event) {
      if (event && (event.target !== panel || event.propertyName !== 'height')) return;
      if (finished) return;
      finished = true;
      accordionClearTween(panel);
      if (done) done();
    }

    panel._shAccOnEnd = finish;
    panel.addEventListener('transitionend', finish);
    panel._shAccTimer = window.setTimeout(finish, ms + 80);
  }

  function collapseAccordion(details) {
    if (!details.classList.contains('is-expanded') && !details.open) return;

    var panel = accordionPanel(details);
    var from = panel ? panel.getBoundingClientRect().height : 0;

    details.classList.remove('is-expanded');

    accordionTween(panel, from, 0, function () {
      if (details.classList.contains('is-expanded')) return;
      details.removeAttribute('open');
      if (panel) {
        panel.style.height = '0px';
        panel.style.transition = '';
      }
    });
  }

  function expandAccordion(details) {
    closeAccordionSiblings(details);

    var panel = accordionPanel(details);
    var from = panel ? panel.getBoundingClientRect().height : 0;

    details.setAttribute('open', '');
    details.classList.add('is-expanded');

    function start() {
      var to = accordionContentHeight(panel);
      accordionTween(panel, from, to, function () {
        if (!details.classList.contains('is-expanded')) return;
        if (panel) {
          panel.style.height = 'auto';
          panel.style.transition = '';
        }
      });
    }

    window.requestAnimationFrame(start);
  }

  function closeAccordionSiblings(details) {
    var name = details.getAttribute('name');
    if (!name) return;

    var others = document.querySelectorAll('details.sh-accordion[name="' + name + '"]');
    for (var i = 0; i < others.length; i++) {
      if (others[i] !== details) collapseAccordion(others[i]);
    }
  }

  function initAccordion(details) {
    if (!details || details.getAttribute(ACC_BOUND) === '1') return;
    details.setAttribute(ACC_BOUND, '1');

    if (details.open) details.classList.add('is-expanded');

    var panel = accordionPanel(details);
    if (panel) {
      panel.style.height = details.classList.contains('is-expanded') ? 'auto' : '0px';
    }

    var summary = details.querySelector(':scope > summary');
    if (!summary) return;

    summary.addEventListener('click', function (event) {
      event.preventDefault();
      if (details.classList.contains('is-expanded')) {
        collapseAccordion(details);
      } else {
        expandAccordion(details);
      }
    });
  }

  function initAccordions(scope) {
    var items = scope.querySelectorAll('details.sh-accordion');
    for (var i = 0; i < items.length; i++) initAccordion(items[i]);
  }

  /* =========================================================================
     Boot
     ========================================================================= */

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;

    var stories = scope.querySelectorAll('.sh-story');
    for (var i = 0; i < stories.length; i++) initStory(stories[i]);

    var badges = scope.querySelectorAll('.jdgm-preview-badge');
    for (var j = 0; j < badges.length; j++) initRatingBadge(badges[j]);

    // The photo block's rating summary scrolls to the reviews the same way the
    // rating block does, sticky-header offset included.
    var ratings = scope.querySelectorAll('.sh-rating, .sh-photos__rating');
    for (var r = 0; r < ratings.length; r++) initRatingScroll(ratings[r]);

    var galleries = scope.querySelectorAll('[data-sh-photos]');
    for (var g = 0; g < galleries.length; g++) initCustomerPhotos(galleries[g]);

    var prices = scope.querySelectorAll('.sh-price');
    for (var p = 0; p < prices.length; p++) initPriceBlock(prices[p]);

    initAccordions(scope);

    var sections = scope.querySelectorAll('.shopify-section--main-product');
    for (var k = 0; k < sections.length; k++) initGallery(sections[k]);

    if (scope !== document && scope.classList && scope.querySelector('.sh-gallery-chrome')) {
      initGallery(scope);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();
